/**
 * Gifts engine — storefront cart gifts driven by shop.custom.gifts_rules.
 *
 * Rules JSON is printed by snippets/gifts-rules.liquid into <script id="GiftsRules">.
 * Checkout UI extension reads the same metafield and acts as a safety net.
 *
 * Flow: any theme/app cart write (/cart/add|change|update|clear) → debounce → GET /cart.js →
 * evaluate(cart, rules) → at most one /cart/update.js + one /cart/add.js → re-render drawer / cart page.
 *
 * Gift lines carry `_gifts_rule = <rule id>` and, when the gift is a paid catalog
 * SKU, `_Gifted` so Shopify keeps them as a separate line from a shopper-paid copy.
 *
 * Debug: localStorage.setItem('giftsEngine:debug', '1') → logs with [GiftsEngine].
 * Console helpers: GiftsEngine.rules, GiftsEngine.evaluate(cart), GiftsEngine.run(), GiftsEngine.dryRun().
 */
(function (root) {
  'use strict';

  var TAG = '_gifts_rule';
  var GIFTED_TAG = '_Gifted';
  var LAST_GOOD_KEY = 'giftsEngine:lastGoodRules';

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function str(value) {
    if (value === null || value === undefined) return '';
    var s = String(value);
    // Accept GIDs (gid://shopify/Product/123) as well as plain ids.
    var slash = s.lastIndexOf('/');
    return slash > -1 ? s.slice(slash + 1) : s;
  }

  function idList(list) {
    return Array.isArray(list) ? list.map(str).filter(Boolean) : [];
  }

  function lineTag(item) {
    var props = item && item.properties;
    return props && props[TAG] ? String(props[TAG]) : '';
  }

  function lineSellingPlanId(item) {
    var alloc = item && item.selling_plan_allocation;
    return alloc && alloc.selling_plan ? str(alloc.selling_plan.id) : '';
  }

  function lineDiscountTitles(item) {
    var titles = [];
    (item.discounts || []).forEach(function (d) {
      if (d && d.title) titles.push(String(d.title));
    });
    (item.line_level_discount_allocations || []).forEach(function (a) {
      var t = a && a.discount_application && a.discount_application.title;
      if (t) titles.push(String(t));
    });
    return titles;
  }

  function truthyProp(value) {
    if (value === true || value === 1) return true;
    var s = String(value == null ? '' : value).toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  // Lines added by the old gift apps (EasyGift `_Gifted`, custom-gift.js `_custom_gift`).
  // Engine gifts also carry `_Gifted`; those are identified by `_gifts_rule` first.
  function isLegacyAppGift(item) {
    if (lineTag(item)) return false;
    var props = (item && item.properties) || {};
    return Object.keys(props).some(function (key) {
      var k = String(key).toLowerCase();
      if (k === '_gifted') return true;
      if (k === '_custom_gift') return truthyProp(props[key]);
      return false;
    });
  }

  // EasyGifts 100%s a line when `_Gifted` equals the offer name (`rule.title`).
  function easyGiftsGiftedValue(rule) {
    var title = rule && rule.title ? String(rule.title).trim() : '';
    if (title && /^offer module\b/i.test(title)) return title;
    return rule && rule.discountTitle ? String(rule.discountTitle) : '';
  }

  function isNestedLine(item) {
    return Boolean(item && item.parent_relationship);
  }

  // Paid SKUs that are also used as gifts (100% off via discountTitle, or the
  // keep-product of a max-one cap). preventOutsideRule must not strip a shopper
  // adding Mini Travel Bottle / Mocktail Shaker at full price.
  function isProtectedPaidProduct(ruleSet, productId) {
    return ((ruleSet && ruleSet.rules) || []).some(function (rule) {
      if (rule.type === 'max_quantity_in_group' && rule.keepProductId === productId) return true;
      return Boolean(
        rule.type === 'add_gift_when_quantity' && rule.discountTitle && rule.giftProductId === productId
      );
    });
  }

  /* ------------------------------------------------------------------ */
  /* Rules: normalize + validate                                         */
  /* ------------------------------------------------------------------ */

  function emptyRuleSet(loadFailed) {
    return { version: 1, rules: [], loadFailed: Boolean(loadFailed) };
  }

  function normalizeOneRule(r, seen) {
    if (!r.id) throw new Error('Gifts Rules: rule without id');
    if (seen[r.id]) throw new Error('Gifts Rules: duplicate id ' + r.id);

    if (r.type === 'add_gift_when_quantity') {
      if (!r.giftVariantId || !r.giftProductId) throw new Error('Gifts Rules: ' + r.id + ' has no gift');
      if (!r.triggerProductId && !idList(r.triggerVariantIds).length) {
        throw new Error('Gifts Rules: ' + r.id + ' has no trigger');
      }
      seen[r.id] = true;
      return {
        id: String(r.id),
        type: r.type,
        title: r.title ? String(r.title) : '',
        triggerProductId: str(r.triggerProductId),
        triggerVariantIds: idList(r.triggerVariantIds),
        triggerSellingPlanIds: idList(r.triggerSellingPlanIds),
        requiresSellingPlan: r.requiresSellingPlan !== false,
        minQuantity: Math.max(1, Number(r.minQuantity) || 1),
        giftProductId: str(r.giftProductId),
        giftVariantId: str(r.giftVariantId),
        giftQuantity: Math.max(1, Number(r.giftQuantity) || 1),
        discountTitle: r.discountTitle ? String(r.discountTitle) : '',
        skipIfProductIds: idList(r.skipIfProductIds),
        removeWhenUnmet: r.removeWhenUnmet !== false,
        preventOutsideRule: r.preventOutsideRule === true,
      };
    }

    if (r.type === 'max_quantity_in_group') {
      var productIds = idList(r.productIds);
      if (!productIds.length) throw new Error('Gifts Rules: ' + r.id + ' has no productIds');
      seen[r.id] = true;
      return {
        id: String(r.id),
        type: r.type,
        productIds: productIds,
        keepProductId: str(r.keepProductId),
        maxQuantity: Math.max(0, Number(r.maxQuantity) || 0),
      };
    }

    return null; // unknown type → ignored
  }

  function normalizeRules(raw) {
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.rules)) {
      throw new Error('Gifts Rules: expected { version, rules: [] }');
    }
    var seen = {};
    var rules = [];
    raw.rules.forEach(function (r) {
      if (!r || r.enabled === false) return;
      try {
        var normalized = normalizeOneRule(r, seen);
        if (normalized) rules.push(normalized);
      } catch (error) {
        console.error('[GiftsEngine] skipping invalid rule', r && r.id, error);
      }
    });
    return { version: raw.version || 1, rules: rules, loadFailed: false };
  }

  function readCachedRules() {
    try {
      return JSON.parse(localStorage.getItem(LAST_GOOD_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  function writeCachedRules(raw) {
    try {
      localStorage.setItem(LAST_GOOD_KEY, JSON.stringify(raw));
    } catch (e) {}
  }

  function clearCachedRules() {
    try {
      localStorage.removeItem(LAST_GOOD_KEY);
    } catch (e) {}
  }

  function loadCachedRuleSet() {
    try {
      return normalizeRules(readCachedRules());
    } catch (e) {
      return emptyRuleSet(true);
    }
  }

  function loadRules() {
    var el = typeof document !== 'undefined' ? document.getElementById('GiftsRules') : null;
    var text = el ? String(el.textContent || '').trim() : '';
    var raw;

    if (!el || text === '' || text === 'null') {
      clearCachedRules();
      return emptyRuleSet(false);
    }

    try {
      raw = JSON.parse(text);
    } catch (error) {
      console.error('[GiftsEngine] Gifts Rules JSON could not be parsed, using last known-good set', error);
      return loadCachedRuleSet();
    }

    if (raw === null || raw === undefined) {
      clearCachedRules();
      return emptyRuleSet(false);
    }

    try {
      var parsed = normalizeRules(raw);
      writeCachedRules(raw);
      return parsed;
    } catch (error) {
      console.error('[GiftsEngine] invalid Gifts Rules, using last known-good set', error);
      return loadCachedRuleSet();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Evaluate (pure): cart + rules → mutation plan                       */
  /* ------------------------------------------------------------------ */

  function matchesTrigger(rule, item) {
    if (lineTag(item) || isNestedLine(item)) return false; // gifts never trigger gifts
    var variantId = str(item.variant_id);
    if (rule.triggerVariantIds.length) {
      if (rule.triggerVariantIds.indexOf(variantId) === -1) return false;
    } else if (str(item.product_id) !== rule.triggerProductId) {
      return false;
    }
    var planId = lineSellingPlanId(item);
    if (rule.requiresSellingPlan) {
      if (!planId) return false;
      if (rule.triggerSellingPlanIds.length && rule.triggerSellingPlanIds.indexOf(planId) === -1) return false;
    }
    return true;
  }

  /**
   * @returns {{ updates: Object<string, number>, adds: Array, log: Array }}
   * updates: line key → new quantity (0 = remove). adds: /cart/add.js items.
   */
  function evaluate(cart, ruleSet) {
    var rules = (ruleSet && ruleSet.rules) || [];
    var addRules = rules.filter(function (r) {
      return r.type === 'add_gift_when_quantity';
    });
    var capRules = rules.filter(function (r) {
      return r.type === 'max_quantity_in_group';
    });
    var log = [];

    // Working copy of the cart. Planned adds are appended as lines without a key.
    var lines = (cart.items || []).map(function (item) {
      return {
        key: item.key,
        productId: str(item.product_id),
        variantId: str(item.variant_id),
        tag: lineTag(item),
        discountTitles: lineDiscountTitles(item),
        legacyGift: isLegacyAppGift(item),
        nested: isNestedLine(item),
        qty: Number(item.quantity) || 0,
        origQty: Number(item.quantity) || 0,
        claimedBy: null,
        item: item,
      };
    });

    function present(productId) {
      return lines.some(function (l) {
        return l.qty > 0 && l.productId === productId;
      });
    }

    // 1. Which add-rules are met.
    var met = {};
    addRules.forEach(function (rule) {
      var qty = 0;
      (cart.items || []).forEach(function (item) {
        if (matchesTrigger(rule, item)) qty += Number(item.quantity) || 0;
      });
      met[rule.id] = qty >= rule.minQuantity;
      log.push([rule.id, met[rule.id] ? 'met' : 'unmet', 'trigger qty ' + qty + '/' + rule.minQuantity]);
    });

    // A line counts as this rule's gift if it is tagged by a rule that gives the same product,
    // or it is an untagged copy carrying this rule's automatic discount (legacy app / Flow line).
    function giftLineFor(rule) {
      var giftRuleIds = addRules
        .filter(function (r) {
          return r.giftProductId === rule.giftProductId;
        })
        .map(function (r) {
          return r.id;
        });
      return lines.filter(function (l) {
        if (l.qty <= 0 || l.nested || l.productId !== rule.giftProductId) return false;
        if (l.tag) return giftRuleIds.indexOf(l.tag) > -1;
        if (l.legacyGift) return true;
        return Boolean(rule.discountTitle) && l.discountTitles.indexOf(rule.discountTitle) > -1;
      });
    }

    // 2. Met rules: keep / fix / add their gift (array order = priority).
    addRules.forEach(function (rule) {
      if (!met[rule.id]) return;

      var own = giftLineFor(rule);
      if (own.length) {
        // Gift already there (own, shared with another rule, or legacy discounted line).
        // One line per gift: prefer a line another rule already kept (shared gift), then our own tag.
        var main =
          own.filter(function (l) {
            return l.claimedBy;
          })[0] ||
          own.filter(function (l) {
            return l.tag === rule.id;
          })[0] ||
          own[0];
        if (!main.claimedBy) {
          main.claimedBy = rule.id;
          if (main.key && main.qty !== rule.giftQuantity) {
            log.push([rule.id, 'fix qty', main.qty + ' → ' + rule.giftQuantity]);
            main.qty = rule.giftQuantity;
          }
        }
        own.forEach(function (l) {
          if (l === main || l.claimedBy) return;
          log.push([rule.id, 'remove', 'duplicate gift line ' + l.productId]);
          l.qty = 0;
          l.claimedBy = rule.id;
        });
        return;
      }

      var blocker = rule.skipIfProductIds.filter(present)[0];
      if (blocker) {
        log.push([rule.id, 'skip', 'product ' + blocker + ' already in cart / planned']);
        return;
      }

      var props = {};
      props[TAG] = rule.id;
      var gifted = easyGiftsGiftedValue(rule);
      if (gifted) props[GIFTED_TAG] = gifted;
      lines.push({
        key: null,
        productId: rule.giftProductId,
        variantId: rule.giftVariantId,
        tag: rule.id,
        discountTitles: [],
        legacyGift: false,
        nested: false,
        qty: rule.giftQuantity,
        origQty: 0,
        claimedBy: rule.id,
        properties: props,
      });
      log.push([rule.id, 'add', 'variant ' + rule.giftVariantId + ' x' + rule.giftQuantity]);
    });

    // 3. Unclaimed gift lines: remove when their rule is unmet / gone.
    // If the live rule set failed to load, leave tagged / legacy gift lines alone.
    var rulesLoadFailed = Boolean(ruleSet && ruleSet.loadFailed);
    var metGiftProducts = addRules
      .filter(function (r) {
        return met[r.id];
      })
      .map(function (r) {
        return r.giftProductId;
      });

    lines.forEach(function (l) {
      if (l.qty <= 0 || l.claimedBy || l.nested) return;
      if (rulesLoadFailed) return;

      if (l.tag) {
        var tagRule = addRules.filter(function (r) {
          return r.id === l.tag;
        })[0];
        // Tag of a deleted / disabled rule, or rule unmet with removeWhenUnmet.
        if (!tagRule || (!met[tagRule.id] && tagRule.removeWhenUnmet) || met[tagRule.id]) {
          log.push([l.tag, 'remove', 'tagged gift line ' + l.productId]);
          l.qty = 0;
        }
        return;
      }

      if (l.legacyGift) {
        log.push(['legacy', 'remove', 'legacy gift line ' + l.productId]);
        l.qty = 0;
        return;
      }

      // Untagged line of a gift product.
      addRules.forEach(function (rule) {
        if (l.qty <= 0 || met[rule.id] || l.productId !== rule.giftProductId) return;
        var hasDiscount = rule.discountTitle && l.discountTitles.indexOf(rule.discountTitle) > -1;
        if (rule.removeWhenUnmet && hasDiscount) {
          log.push([rule.id, 'remove', 'discounted untagged gift ' + l.productId]);
          l.qty = 0;
        } else if (
          rule.preventOutsideRule &&
          metGiftProducts.indexOf(rule.giftProductId) === -1 &&
          !isProtectedPaidProduct(ruleSet, rule.giftProductId)
        ) {
          log.push([rule.id, 'remove', 'outside-rule copy ' + l.productId]);
          l.qty = 0;
        }
      });
    });

    // Catalog SKUs that are also gifts (Mini Travel Bottle, Mocktail Shaker).
    // Shopper-paid copies must not count toward gift caps or be treated as gifts.
    function isCatalogGiftProduct(productId) {
      return addRules.some(function (r) {
        return Boolean(r.discountTitle) && r.giftProductId === productId;
      });
    }

    function hasGiftDiscount(l) {
      return (l.discountTitles || []).some(function (title) {
        return addRules.some(function (r) {
          return r.discountTitle && r.discountTitle === title;
        });
      });
    }

    function isShopperPaidLine(l) {
      if (l.tag || l.legacyGift || l.claimedBy) return false;
      if (hasGiftDiscount(l)) return false;
      return isCatalogGiftProduct(l.productId);
    }

    // 4. Group caps (keep product first; within it, gift lines first).
    // Shopper-paid copies of catalog gift SKUs stay — a free Mini/Shaker gift
    // plus another bottle/shaker bought at full price is allowed.
    capRules.forEach(function (rule) {
      var group = lines.filter(function (l) {
        return l.qty > 0 && !l.nested && rule.productIds.indexOf(l.productId) > -1 && !isShopperPaidLine(l);
      });
      var total = group.reduce(function (sum, l) {
        return sum + l.qty;
      }, 0);
      if (total <= rule.maxQuantity) return;

      function rank(l) {
        return (l.productId === rule.keepProductId ? 0 : 2) + (l.tag || l.claimedBy ? 0 : 1);
      }
      group.sort(function (a, b) {
        return rank(a) - rank(b);
      });

      var hasKeepProduct = group.some(function (l) {
        return l.productId === rule.keepProductId;
      });
      var left = rule.maxQuantity;
      group.forEach(function (l) {
        var keep = Math.min(l.qty, left);
        if (hasKeepProduct && l.productId !== rule.keepProductId) keep = 0; // non-keep lines are removed
        left -= keep;
        if (keep !== l.qty) {
          log.push([rule.id, 'cap', l.productId + ' ' + l.qty + ' → ' + keep]);
          l.qty = keep;
        }
      });
    });

    // 5. Build mutation plan.
    var updates = {};
    var adds = [];
    lines.forEach(function (l) {
      if (l.key) {
        if (l.qty !== l.origQty) updates[l.key] = l.qty;
      } else if (l.qty > 0) {
        adds.push({ id: Number(l.variantId), quantity: l.qty, properties: l.properties });
      }
    });

    return { updates: updates, adds: adds, log: log };
  }

  function planIsEmpty(plan) {
    return !plan.adds.length && !Object.keys(plan.updates).length;
  }

  /* ------------------------------------------------------------------ */
  /* Runner (browser only)                                               */
  /* ------------------------------------------------------------------ */

  function createRunner(win) {
    var doc = win.document;
    var nativeFetch = win.fetch.bind(win);
    var DEBOUNCE_MS = 150;
    var MAX_PASSES = 3;
    var CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

    var ruleSet = loadRules();
    var debug = false;
    try {
      debug = localStorage.getItem('giftsEngine:debug') === '1';
    } catch (e) {}
    function log() {
      if (!debug) return;
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[GiftsEngine]');
      console.log.apply(console, args);
    }

    // Single write lock shared by theme cart writes and engine mutations.
    var lock = Promise.resolve();
    function withLock(task) {
      var run = lock.then(task, task);
      lock = run.catch(function () {});
      return run;
    }

    var timer = null;
    var running = null;
    var dirty = false;
    var restoring = null;
    var wasInert = false;

    // Latest full cart JSON we already have (page snapshot, /cart/change|update responses).
    // Lets the engine skip a GET /cart.js round-trip.
    var knownCart = readJson('GiftsCart');
    // Cart the shopper's DOM currently represents. Used to rewrite queued `line`
    // index writes to line keys while the engine is mutating the cart.
    var shopperCart = knownCart;
    var lastEvaluatedCart = knownCart;
    var renderedSections = null; // sections HTML returned with the engine's last mutation
    var failedAdds = {}; // gift variants that could not be added (sold out…) — not retried on this page
    var SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
    var writeSeq = 0;

    function readJson(id) {
      var el = doc.getElementById(id);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent || 'null');
      } catch (e) {
        return null;
      }
    }

    function isFullCart(data) {
      return Boolean(data && Array.isArray(data.items) && typeof data.item_count === 'number');
    }

    function cartRequest(url, body) {
      return nativeFetch(url, {
        method: body ? 'POST' : 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: body
          ? { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Gifts-Engine': '1' }
          : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    }

    function getCart() {
      return cartRequest((win.routes && win.routes.cart_url ? win.routes.cart_url : '/cart') + '.js');
    }

    function withSections(body) {
      if (!doc.querySelector('#shopify-section-cart-drawer')) return body;
      body.sections = SECTIONS;
      body.sections_url = win.location.pathname;
      return body;
    }

    // Resolves with the full cart when the last request returned it (update.js), otherwise null.
    function applyPlan(plan) {
      var hasUpdates = Object.keys(plan.updates).length > 0;
      var chain = Promise.resolve(null);
      if (hasUpdates) {
        chain = chain.then(function () {
          var body = { updates: plan.updates };
          return cartRequest('/cart/update.js', plan.adds.length ? body : withSections(body)).then(function (cart) {
            if (cart && cart.sections) renderedSections = cart.sections;
            return cart;
          });
        });
      }
      if (plan.adds.length) {
        chain = chain.then(function () {
          return cartRequest('/cart/add.js', withSections({ items: plan.adds }))
            .then(function (res) {
              if (res && res.sections) renderedSections = res.sections;
              return null; // add.js does not return the whole cart
            })
            .catch(function (error) {
              // e.g. gift sold out — never break the cart because of a gift.
              plan.adds.forEach(function (a) {
                failedAdds[a.id] = true;
              });
              console.warn('[GiftsEngine] could not add gift', plan.adds, error.data || error);
              return null;
            });
        });
      }
      return chain;
    }

    function refreshUi(strict) {
      var tasks = [];
      var sections = renderedSections;
      renderedSections = null;
      if (typeof win.renderCartDrawer === 'function' && doc.querySelector('#shopify-section-cart-drawer')) {
        tasks.push(Promise.resolve(win.renderCartDrawer({ sections: sections })).catch(function (error) {
          if (strict) throw error;
        }));
      }
      var mainCart = doc.querySelector('cart-items');
      if (mainCart && typeof mainCart.onCartUpdate === 'function') {
        tasks.push(Promise.resolve(mainCart.onCartUpdate()).catch(function () {}));
      }
      return Promise.all(tasks).then(function () {
        doc.dispatchEvent(new CustomEvent('gifts:updated'));
      });
    }

    function reconcile(forceRefresh) {
      return withLock(function () {
        if (forceRefresh) {
          knownCart = null;
          renderedSections = null;
          failedAdds = {};
        }
        var changed = false;
        var pass = 0;
        function step(cartFromLastRequest) {
          pass += 1;
          var source = cartFromLastRequest || knownCart;
          knownCart = null;
          return (isFullCart(source) ? Promise.resolve(source) : getCart()).then(function (cart) {
            lastEvaluatedCart = cart;
            var plan = evaluate(cart, ruleSet);
            plan.adds = plan.adds.filter(function (a) {
              return !failedAdds[a.id];
            });
            if (debug) {
              var actions = plan.log.filter(function (r) {
                return r[1] !== 'met' && r[1] !== 'unmet';
              });
              console.groupCollapsed(
                '[GiftsEngine] pass ' + pass + ': ' + (planIsEmpty(plan) ? 'cart is up to date' : actions.length + ' change(s)')
              );
              console.table(
                plan.log.map(function (r) {
                  return { rule: r[0], action: r[1], details: r[2] };
                })
              );
              console.groupEnd();
            }
            if (planIsEmpty(plan) || pass > MAX_PASSES) {
              if (!changed) renderedSections = null;
              return changed;
            }
            changed = true;
            return applyPlan(plan).then(step);
          });
        }
        return step();
      })
        .then(function (changed) {
          if (!changed && !forceRefresh) return null;
          return refreshUi(forceRefresh).then(function () {
            if (isFullCart(lastEvaluatedCart)) shopperCart = lastEvaluatedCart;
          });
        })
        .catch(function (error) {
          console.error('[GiftsEngine] reconcile failed', error);
          if (forceRefresh) throw error;
        });
    }

    function run() {
      if (running) {
        dirty = true;
        return running;
      }
      running = reconcile().then(function () {
        running = null;
        if (dirty) {
          dirty = false;
          return run();
        }
      });
      return running;
    }

    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        run();
      }, DEBOUNCE_MS);
    }

    function flush() {
      if (restoring) return restoring;
      if (timer) {
        clearTimeout(timer);
        timer = null;
        return run();
      }
      return running || Promise.resolve();
    }

    function isPending() {
      return Boolean(restoring || timer || running);
    }

    function parseJsonBody(init) {
      if (!init || typeof init.body !== 'string') return null;
      try {
        return JSON.parse(init.body);
      } catch (e) {
        return null;
      }
    }

    // Theme cart.js posts { line: 1-basedIndex }. After the engine adds/removes a
    // gift, that index points at a different row. Rewrite to the line key the
    // shopper actually clicked, from the cart currently painted in the DOM.
    function rewriteLineChangeToId(init, cart) {
      var body = parseJsonBody(init);
      if (!body || body.id || !body.line || !cart || !Array.isArray(cart.items)) return init;
      var item = cart.items[Number(body.line) - 1];
      if (!item || !item.key) return init;
      var next = {};
      Object.keys(body).forEach(function (key) {
        if (key !== 'line') next[key] = body[key];
      });
      next.id = item.key;
      return Object.assign({}, init, { body: JSON.stringify(next) });
    }

    /* Hook every cart write made with fetch (theme + apps). */
    win.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var headers = (init && init.headers) || {};
      var internal =
        (typeof headers.get === 'function' && headers.get('X-Gifts-Engine')) || headers['X-Gifts-Engine'];

      if (method !== 'POST' || internal || !CART_WRITE_RE.test(url)) {
        return nativeFetch(input, init);
      }

      var queuedInit = /\/cart\/change(\.js|\.json)?(\?|$)/.test(url)
        ? rewriteLineChangeToId(init, shopperCart)
        : init;

      return withLock(function () {
        return nativeFetch(input, queuedInit);
      }).then(function (response) {
        knownCart = null;
        var seq = ++writeSeq;
        // change.js / update.js answer with the whole cart — reuse it instead of GET /cart.js.
        if (response && response.ok && /\/cart\/(change|update)\.js/.test(url)) {
          response
            .clone()
            .json()
            .then(function (data) {
              if (isFullCart(data) && seq === writeSeq) {
                knownCart = data;
                shopperCart = data;
              }
            })
            .catch(function () {})
            .then(schedule);
        } else {
          schedule();
        }
        return response;
      });
    };

    function liveCheckoutForm() {
      var button =
        doc.getElementById('CartDrawer-Checkout') ||
        doc.getElementById('checkout') ||
        doc.querySelector('[name="checkout"]');
      if (button && button.form) return button.form;
      return doc.getElementById('CartDrawer-Form') || doc.getElementById('cart') || null;
    }

    function liveCheckoutSubmitter(form) {
      if (form) {
        var nested = form.querySelector('[name="checkout"]');
        if (nested) return nested;
        if (form.id) {
          var associated = doc.querySelector('[name="checkout"][form="' + form.id + '"]');
          if (associated) return associated;
        }
      }
      return doc.getElementById('CartDrawer-Checkout') || doc.getElementById('checkout') || null;
    }

    function submitCheckout(form, submitter) {
      var liveForm = (form && form.isConnected && form) || liveCheckoutForm();
      var liveSubmitter =
        (submitter && submitter.isConnected && submitter) || liveCheckoutSubmitter(liveForm);
      if (liveForm && typeof liveForm.requestSubmit === 'function') {
        liveForm.requestSubmit(liveSubmitter || undefined);
        return;
      }
      if (liveForm) {
        liveForm.submit();
        return;
      }
      win.location.href = '/checkout';
    }

    /* Settle gifts before leaving for checkout. */
    function onCheckoutSubmit(event) {
      var submitter = event.submitter;
      var isCheckout =
        (submitter && submitter.name === 'checkout') ||
        (event.target && event.target.querySelector && event.target.querySelector('[name="checkout"]:focus'));
      if (!isCheckout || !isPending()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      flush().then(function () {
        submitCheckout(event.target, submitter);
      });
    }

    function onCheckoutLinkClick(event) {
      var link = event.target && event.target.closest && event.target.closest('a[href*="/checkout"]');
      if (!link || !isPending()) return;
      var href = link.href;
      event.preventDefault();
      flush().then(function () {
        win.location.href = href;
      });
    }

    doc.addEventListener('submit', onCheckoutSubmit, true);
    doc.addEventListener('click', onCheckoutLinkClick, true);

    // Preserve PDP state on history navigation. Freeze edits until suspended
    // work has settled and a fresh cart has been evaluated and painted.
    win.addEventListener('pagehide', function (event) {
      if (!event.persisted) return;
      wasInert = doc.documentElement.inert;
      doc.documentElement.inert = true;
      clearTimeout(timer);
      timer = null;
    });
    win.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      restoring = Promise.resolve(running).then(function () {
        return reconcile(true);
      }).then(function () {
        doc.documentElement.inert = wasInert;
        restoring = null;
      }).catch(function (error) {
        console.error('[GiftsEngine] restore failed', error);
        win.location.reload();
      });
    });
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', schedule);
    else schedule();

    return {
      get rules() {
        return ruleSet;
      },
      evaluate: function (cart) {
        return evaluate(cart, ruleSet);
      },
      dryRun: function () {
        return getCart().then(function (cart) {
          var plan = evaluate(cart, ruleSet);
          console.table(plan.log.map(function (r) {
            return { rule: r[0], action: r[1], details: r[2] };
          }));
          return plan;
        });
      },
      run: run,
      flush: flush,
      isPending: isPending,
      reloadRules: function () {
        ruleSet = loadRules();
        return ruleSet;
      },
    };
  }

  /* ------------------------------------------------------------------ */
  /* Exports                                                             */
  /* ------------------------------------------------------------------ */

  var api = {
    normalizeRules: normalizeRules,
    evaluate: evaluate,
    planIsEmpty: planIsEmpty,
    TAG: TAG,
    GIFTED_TAG: GIFTED_TAG,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api; // Node tests
  } else if (root && root.document && !root.GiftsEngine) {
    var runner = createRunner(root);
    runner.normalizeRules = normalizeRules;
    runner.evaluateWith = evaluate;
    runner.TAG = TAG;
    runner.GIFTED_TAG = GIFTED_TAG;
    root.GiftsEngine = runner;
  }
})(typeof window !== 'undefined' ? window : this);
